import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/lib/api/client'
import type { ApiResponse } from '@/lib/api/types'
import { useAuthStore } from '@/stores/authStore'

export interface NotificationItem {
  id: number
  type: string
  title: string
  message: string
  link: string | null
  isRead: boolean
  createdAt: string
}

interface PageResponse {
  content: NotificationItem[]
  totalElements: number
  totalPages: number
  number: number
  size: number
  last: boolean
}

/** 내 알림 목록 */
export function useNotifications(page = 0) {
  return useQuery({
    queryKey: ['notifications', page],
    queryFn: async () => {
      const res = await apiClient.get<ApiResponse<PageResponse>>(
        `/notifications?page=${page}&size=20`
      )
      return res.data.data
    },
    retry: 1,
  })
}

/** 안읽은 알림 수 (30초 폴링) */
export function useUnreadCount() {
  // access token 이 실제로 존재해야 호출. 그렇지 않으면 401 → refresh 401 의
  // 초기 로드 노이즈가 콘솔에 남음. token 이 없거나 만료된 상태에서는
  // 폴링 자체를 비활성화하고, interceptor 의 refresh 흐름에 맡긴다.
  const hasValidToken = useAuthStore(
    (s) => s.isAuthenticated && !!s.accessToken
  )
  return useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: async () => {
      const res = await apiClient.get<ApiResponse<{ count: number }>>('/notifications/unread-count')
      return res.data.data.count
    },
    enabled: hasValidToken,
    refetchInterval: 30000,
    retry: false,
  })
}

/** 알림 읽음 처리 */
export function useMarkAsRead() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.patch(`/notifications/${id}/read`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  })
}

/** 전체 읽음 처리 */
export function useMarkAllAsRead() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      await apiClient.patch('/notifications/read-all')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  })
}
